import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Circle,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  type StyleProps,
  Text,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { useMarkAllNotificationsAsReadMutation } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import type { NotificationsStore } from "./store";

export const NotificationSettings: FC<{ store: NotificationsStore }> = observer(
  function NotificationSettings({ store }) {
    const [{ fetching: marking }, markAll] =
      useMarkAllNotificationsAsReadMutation();
    const { isMobile } = useScreenType();

    const ActionCheckbox = ({ w }: { w?: StyleProps["width"] }) => {
      return (
        <Checkbox
          w={w}
          isChecked={store.actionRequired}
          onChange={store.toggleActionRequired}
        >
          <Box display="inline-flex">
            <Trans>Action required</Trans>
            {store.showActionNotificationCount && (
              <Text color="red.500">
                &nbsp;({store.unreadActionNotifications})
              </Text>
            )}
          </Box>
        </Checkbox>
      );
    };

    const InfoCheckbox = ({ w }: { w?: StyleProps["width"] }) => {
      return (
        <Checkbox
          w={w}
          isChecked={store.infoAlerts}
          onChange={store.toggleInfoAlerts}
        >
          <Box display="inline-flex">
            <Trans>Information alerts</Trans>
            {store.showInfoNotificationCount && (
              <Text color="red.500">
                &nbsp;({store.unreadInfoNotifications})
              </Text>
            )}
          </Box>
        </Checkbox>
      );
    };

    return (
      <WrapComponent
        if={!isMobile}
        with={(children) => (
          <HStack w="full" ml="2">
            <HStack w="full" spacing="2">
              <ActionCheckbox />
              <InfoCheckbox />
            </HStack>
            <HStack spacing="2">
              <ButtonGroup size="xs" spacing="0">
                <Button
                  isLoading={store.isLoading}
                  onClick={() => {
                    store.setShowReadNotifications(true);
                  }}
                  roundedRight="none"
                  variant={store.showReadNotifications ? "solid" : "outline"}
                >
                  <Trans>All</Trans>
                </Button>
                <Button
                  isLoading={store.isLoading}
                  onClick={() => {
                    store.setShowReadNotifications(false);
                  }}
                  roundedLeft="none"
                  variant={store.showReadNotifications ? "outline" : "solid"}
                >
                  <Trans>Unread</Trans>
                </Button>
              </ButtonGroup>
              {children}
            </HStack>
          </HStack>
        )}
      >
        <Menu>
          <MenuButton>
            {marking ? (
              <Spinner color="teal.500" size="xs" />
            ) : (
              <Box>
                <Icon name="dots-horizontal" />
                {store.totalNotificationCount > 0 && (
                  <Circle
                    pos="absolute"
                    top="0"
                    right="1"
                    bg={"red.500"}
                    size="5"
                  >
                    <Text color="white" fontSize="2xs" fontWeight="bold">
                      {store.totalNotificationCount}
                    </Text>
                  </Circle>
                )}
              </Box>
            )}
          </MenuButton>
          <MenuList>
            {isMobile && (
              <MenuItem closeOnSelect={false}>
                <ActionCheckbox w="full" />
              </MenuItem>
            )}
            {isMobile && (
              <MenuItem closeOnSelect={false}>
                <InfoCheckbox w="full" />
              </MenuItem>
            )}
            {isMobile && (
              <MenuItem closeOnSelect={false}>
                <Checkbox
                  w="full"
                  isChecked={!store.showReadNotifications}
                  onChange={store.toggleShowUnread}
                >
                  <Trans>Unread only</Trans>
                </Checkbox>
              </MenuItem>
            )}
            {Boolean(store.notifications.length) && (
              <MenuItem
                onClick={() => {
                  markAll({
                    groups: store.notificationGroups,
                  }).then(() => {
                    store.refetch?.();
                    runInAction(() => {
                      store.setUnreadNotificationCount(0);
                    });
                  });
                }}
              >
                <Text fontSize="md">
                  <Trans>Mark all as read</Trans>
                </Text>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </WrapComponent>
    );
  },
);
